import dayjs from "dayjs";

import { ChevronDown, GoToPage } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TextCell, TextAnchorCell, customRangeFormat, fullMonth, fullYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, numberFilter } from "app/filters/ngFilters";
import { removeProtocolAndWww } from "app/pages/digital-marketing/utils/url";
import { createMultiIncludeExcludeFilter } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/utils";
import { allTrackers } from "app/services/track/track";
import { searchAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/searchAdvertising/searchAdvertisingNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { useAdvertiserActivityDataAndParams } from "../../common/hooks/useAdvertiserActivityDataAndParams";
import { NA_VALUE } from "../../common/utils/utils";

import { DrilldownWrapper } from "app/components/keyword-research/landing-pages/styledComponents";
import {
    ExpandedRowContent,
    MonthRow,
    SeeAdsLink,
    StyledEnrichButtonWrapper,
    YearColumn,
    YearsContainer,
} from "./StyledComponents";
import type { ITransformedSearchCampaignData } from "./types";

const SELECTED_PAGE_TAB = "Text";

const buildNavigationParams = (landingPageUrl: string, duration: string) => {
    const currentParams = proNavigator.getParams();

    // Remove protocol and www from the URL to match filter format
    const cleanUrl = removeProtocolAndWww(landingPageUrl);

    return {
        ...currentParams,
        duration,
        selectedPageTab: SELECTED_PAGE_TAB,
        multiIncludeExcludeUrl: createMultiIncludeExcludeFilter(cleanUrl, "url"),
        ignoreFilterConsistency: true,
    };
};

export const getAdsPageUrl = (landingPageUrl: string, duration: string): string => {
    const navigationParams = buildNavigationParams(landingPageUrl, duration);
    return searchAdvertisingNavigation.textAndShoppingAds.getLink(navigationParams);
};

const navigateToAdsPage = (landingPageUrl: string, duration: string) => {
    const navigationParams = buildNavigationParams(landingPageUrl, duration);
    void searchAdvertisingNavigation.textAndShoppingAds.go(navigationParams);
};

export const getColumns = (
    toggleExpandRow: (rowKey: string) => void,
    isOneMonthDuration: boolean,
    params: ReturnType<typeof useAdvertiserActivityDataAndParams>,
): ColumnsType<ITransformedSearchCampaignData> => {
    const columns: ColumnsType<ITransformedSearchCampaignData> = [
        {
            title: (
                <TextCell
                    fontVariant="captionTitle"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("ad.intelligence.advertiser.search.campaigns.campaign.name.column.title")}
                </TextCell>
            ),
            dataIndex: "campaignTitle",
            render: (title: string) => <TextCell>{title || NA_VALUE}</TextCell>,
        },
        {
            title: translate("ad.intelligence.advertiser.search.campaigns.landing.page.column.title"),
            dataIndex: "url",
            render: (url: string) => {
                const formattedUrl = /^https?:\/\//.test(url) ? url : `https://${url}`;
                return (
                    <TextAnchorCell
                        target="_blank"
                        href={formattedUrl}
                        onClick={() => {
                            allTrackers.dispatchTrackEvent("External Link", "click", `Table/URL/${String(url)}`);
                        }}
                    >
                        {url}
                    </TextAnchorCell>
                );
            },
        },
        {
            title: translate("ad.intelligence.advertiser.search.campaigns.ads.column.title"),
            dataIndex: "uniqueAdsCount",
            width: 100,
            align: isOneMonthDuration ? "right" : "left",
            render: (count: number, record: ITransformedSearchCampaignData) => {
                const displayValue = !count ? NA_VALUE : abbrNumberFilter()(count);

                const onClick = () => {
                    if (isOneMonthDuration) {
                        allTrackers.dispatchTrackEvent("Internal Link", "click", "Search Campaigns/Ads");
                        navigateToAdsPage(record.url, params.duration);
                    } else if (record.monthsAppeared?.length > 0) {
                        allTrackers.dispatchTrackEvent("Button", "click", "Search Campaigns/months drilldown");
                        toggleExpandRow(record.key);
                    }
                };

                return count ? (
                    <Tooltip
                        content={translate("ad.intelligence.search.campaigns.ad.count.cell.tooltip.title")}
                        enabled={isOneMonthDuration}
                    >
                        {isOneMonthDuration ? (
                            <TextAnchorCell onClick={onClick}>{displayValue}</TextAnchorCell>
                        ) : (
                            <DrilldownWrapper onClick={onClick}>
                                <StyledEnrichButtonWrapper>
                                    <ChevronDown
                                        size="xs"
                                        color={colorsPalettes.carbon[400]}
                                    />
                                    <Text
                                        fontVariant="input2"
                                        color={colorsPalettes.carbon[400]}
                                    >
                                        {displayValue}
                                    </Text>
                                </StyledEnrichButtonWrapper>
                            </DrilldownWrapper>
                        )}
                    </Tooltip>
                ) : (
                    <TextCell>{displayValue}</TextCell>
                );
            },
        },
    ];

    return columns;
};

export const renderExpandedRow = (record: ITransformedSearchCampaignData, toggleExpandRow: () => void) => {
    // Group months by year
    const monthsByYear: Record<string, string[]> = {};

    record.monthsAppeared?.forEach((month) => {
        const year = dayjs(month).format(fullYear);
        if (!monthsByYear[year]) {
            monthsByYear[year] = [];
        }
        monthsByYear[year].push(month);
    });

    // Sort years in descending order and sort months within each year in descending order
    const sortedYears = Object.keys(monthsByYear).sort((a, b) => Number(b) - Number(a));

    // Sort months within each year in descending order (newest first)
    sortedYears.forEach((year) => {
        monthsByYear[year].sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf());
    });

    return (
        <ExpandedRowContent>
            <Flex
                height="48px"
                alignItems="center"
                justifyContent="space-between"
                borderBottom={`1px solid ${colorsPalettes.carbon[50]}`}
                paddingX="24px"
            >
                <Text
                    fontVariant="tab"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("ad.intelligence.advertiser.search.campaigns.ads.monthly.breakdown.title", {
                        count: numberFilter()(record.uniqueAdsCount ?? 0),
                    })}
                </Text>
                <IconButton
                    type="flat"
                    iconName="clear"
                    iconSize="xs"
                    onClick={toggleExpandRow}
                />
            </Flex>
            <YearsContainer>
                {sortedYears.map((year) => (
                    <YearColumn key={year}>
                        <Text
                            fontVariant="tab"
                            color={colorsPalettes.carbon[300]}
                            margin="0 0 8px"
                        >
                            {year}
                        </Text>
                        {monthsByYear[year].map((month, index) => (
                            <MonthRow key={`${month}-${index}`}>
                                <Text
                                    fontVariant="subtitle2"
                                    color={colorsPalettes.carbon[300]}
                                    flex="1"
                                >
                                    {dayjs(month).format(fullMonth)}
                                </Text>
                                <SeeAdsLink
                                    onClick={() => {
                                        const monthFormatted = dayjs(month).format(customRangeFormat);
                                        const monthDuration = `${monthFormatted}-${monthFormatted}`;

                                        allTrackers.dispatchTrackEvent(
                                            "Internal Link",
                                            "click",
                                            `Months/${month}/See ads`,
                                        );
                                        navigateToAdsPage(record.url, monthDuration);
                                    }}
                                >
                                    {translate("ad.intelligence.advertiser.search.campaigns.see.ads")}
                                    <GoToPage
                                        size="xs"
                                        color={colorsPalettes.blue[400]}
                                    />
                                </SeeAdsLink>
                            </MonthRow>
                        ))}
                    </YearColumn>
                ))}
            </YearsContainer>
        </ExpandedRowContent>
    );
};
